import React, { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { projects, certifications, blogs, workExperience } from '../data';

function Terminal({ openWindow }) {
  const [history, setHistory] = useState([
    { type: 'output', text: 'Welcome to Kali GNU/Linux 2026.2 (qterminal)' },
    { type: 'output', text: 'Type "help" to see available commands, or click the quick-links below.' },
    { type: 'output', text: '' },
  ]);
  const [input, setInput] = useState('');
  const [matrixActive, setMatrixActive] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom whenever history changes
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [history]);

  // Focus terminal input when clicking the terminal container
  const handleTerminalClick = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const executeCommand = (cmdText) => {
    const trimmed = cmdText.trim();
    if (!trimmed) return;

    const parts = trimmed.split(' ');
    const cmd = parts[0].toLowerCase();
    const arg = parts.slice(1).join(' ');

    const newHistory = [...history, { type: 'prompt', text: `root@kali:~# ${trimmed}` }];

    switch (cmd) {
      case 'help':
        newHistory.push({
          type: 'output',
          text: `
Available commands:
  neofetch       Display system information
  whoami         Print biography details
  ls             List contents of current directory
  cat <file>     Display contents of a file (e.g. cat projects.txt)
  projects       Launch GUI Projects window
  certs          Launch GUI Certifications window
  blogs          Launch GUI Technical Blogs window
  experience     Launch GUI Work Experience window
  contact        Launch GUI Contact window
  nmap           Simulate network scan on target portfolio
  matrix         Run Matrix code rain visualizer
  cowsay [msg]   Let the cow speak!
  clear          Clear terminal window
`
        });
        break;

      case 'neofetch':
        newHistory.push({
          type: 'output',
          text: `
              .
             / \\
            /   \\
           /     \\
    ,-.   /       \\   ,-.
   /   \\ /         \\\\ /   \\
   \\    Y           Y    /
    \\   |   K A L I |   /
     \\  |           |  /
      \\ |   L I N U X |/
       \\ \\         / /
        \\ \\       / /
         \\ \\     / /
          \\ \\   / /
           \\ \\ / /
            \\ V /
             '
             
root@kali
---------
OS: Kali GNU/Linux 2026.2
Host: Tathagata Roy Portfolio v2.0
Kernel: 6.8.0-kali-amd64
Shell: zsh 5.9
DE: XFCE 4.18
WM: Xfwm4
Terminal: qterminal
Skills: Pentesting, Malware Reversing, React, GRC
`
        });
        break;

      case 'whoami':
        newHistory.push({
          type: 'output',
          text: `
Name: Tathagata Roy
Role: Cloud Security Analyst // Web Developer
Bio: Securing applications, conducting malware audit research, and developing interactive, high-performance web systems.
Motto: "Security is not a product, but a process."
Type "cat biography.txt" for more details.
`
        });
        break;

      case 'ls':
        newHistory.push({
          type: 'output',
          text: `
drwxr-xr-x  2 root root 4096 Jun 24 01:43 .
drwxr-xr-x  3 root root 4096 Jun 24 01:43 ..
-rw-r--r--  1 root root  820 Jun 24 01:43 biography.txt
-rw-r--r--  1 root root 1450 Jun 24 01:43 projects.txt
-rw-r--r--  1 root root  980 Jun 24 01:43 certifications.txt
-rw-r--r--  1 root root  890 Jun 24 01:43 blogs.txt
-rw-r--r--  1 root root 1200 Jun 24 01:43 experience.txt
-rw-r--r--  1 root root  430 Jun 24 01:43 contact.txt
`
        });
        break;

      case 'cat':
        if (!arg) {
          newHistory.push({ type: 'error', text: 'Usage: cat <filename>' });
        } else {
          const file = arg.toLowerCase();
          if (file === 'biography.txt') {
            newHistory.push({
              type: 'output',
              text: `
TATHAGATA ROY
Cloud Security Analyst & Web Developer

Welcome to my secure terminal. I focus on securing cloud-native applications, analyzing trojans/malware families, conducting vulnerability reviews in complex codebases (Python/JS/Rust/Go), and constructing responsive, sleek interfaces.

Active certification badge verifications:
- API Penetration Testing
- arcX Cyber Threat Intelligence (CTI)
- Qualys Vulnerability Management
- EC-Council Ethical Hacking Essentials
`
            });
          } else if (file === 'projects.txt') {
            const list = projects.map(p => `• ${p.title} (${p.tags.join(', ')})\n  Desc: ${p.description}\n  URL: ${p.link}`).join('\n\n');
            newHistory.push({ type: 'output', text: `\nPROJECTS DIRECTORY:\n\n${list}\n` });
          } else if (file === 'certifications.txt') {
            const list = certifications.map(c => `• ${c.title} [${c.tags.join('/')}]`).join('\n');
            newHistory.push({ type: 'output', text: `\nCERTIFICATIONS:\n\n${list}\n` });
          } else if (file === 'blogs.txt') {
            const list = blogs.map(b => `• ${b.title}\n  Desc: ${b.description}\n  URL: ${b.link}`).join('\n\n');
            newHistory.push({ type: 'output', text: `\nPUBLISHED WRITINGS:\n\n${list}\n` });
          } else if (file === 'experience.txt') {
            const list = workExperience.map(w => `• ${w.role} at ${w.company} (${w.duration})\n  Skills: ${w.skills.join(', ')}`).join('\n\n');
            newHistory.push({ type: 'output', text: `\nPROFESSIONAL TIMELINE:\n\n${list}\n` });
          } else if (file === 'contact.txt') {
            newHistory.push({
              type: 'output',
              text: `
FORWARDING PROTOCOLS:
- LinkedIn: https://www.linkedin.com/in/roy9495/
- GitHub: https://github.com/roy9495
- Twitter/X: https://x.com/royzen9495

Use the "contact" command to open the secure message transmitting GUI.
`
            });
          } else {
            newHistory.push({ type: 'error', text: `cat: ${arg}: No such file or directory` });
          }
        }
        break;

      case 'projects':
        newHistory.push({ type: 'output', text: 'Opening Projects Window...' });
        openWindow('projects');
        break;

      case 'certs':
        newHistory.push({ type: 'output', text: 'Opening Certifications Window...' });
        openWindow('certifications');
        break;

      case 'blogs':
        newHistory.push({ type: 'output', text: 'Opening Technical Blogs Window...' });
        openWindow('blogs');
        break;

      case 'experience':
        newHistory.push({ type: 'output', text: 'Opening Work Experience Window...' });
        openWindow('experience');
        break;

      case 'contact':
        newHistory.push({ type: 'output', text: 'Opening Contact Portal Window...' });
        openWindow('contact');
        break;

      case 'clear':
        setHistory([]);
        setInput('');
        return;

      case 'cowsay':
        const cowMsg = arg || 'Hack the Planet!';
        newHistory.push({
          type: 'output',
          text: `
 ________________________________________
< ${cowMsg} >
 ----------------------------------------
        \\   ^__^
         \\  (oo)\\_______
            (__)\\       )\\/\\
                ||----w |
                ||     ||
`
        });
        break;

      case 'nmap':
        newHistory.push({ type: 'output', text: 'Initiating scan...' });
        setHistory([...newHistory, { type: 'output', text: 'Starting Nmap 7.94 ( https://nmap.org ) at 2026-06-24 01:43 UTC' }]);
        setTimeout(() => {
          setHistory(prev => [
            ...prev,
            { type: 'output', text: 'Nmap scan report for portfolio.tathagata.roy (185.199.108.153)\nHost is up (0.015s latency).\n\nPORT     STATE SERVICE VERSION\n22/tcp   open  ssh     OpenSSH 9.2p1\n80/tcp   open  http    nginx 1.22.1 (Tathagata Portfolio)\n443/tcp  open  ssl/http nginx 1.22.1\n3000/tcp open  react   React Dev Server (Interactive UI v2.0)' },
            { type: 'output', text: '\nNmap done: 1 IP address (1 host up) scanned in 1.45 seconds' }
          ]);
        }, 1200);
        setInput('');
        return;

      case 'matrix':
        newHistory.push({ type: 'output', text: 'Initializing neural link...' });
        setMatrixActive(true);
        setTimeout(() => {
          setMatrixActive(false);
          setHistory(prev => [...prev, { type: 'output', text: 'Neural link terminated.' }]);
        }, 4000);
        setInput('');
        return;

      default:
        newHistory.push({ type: 'error', text: `zsh: command not found: ${cmd}. Type "help" for a list of commands.` });
    }

    setHistory(newHistory);
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      executeCommand(input);
    }
  };

  // Matrix code rain lines simulation
  useEffect(() => {
    if (!matrixActive) return;
    const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ$#@%&';
    const interval = setInterval(() => {
      let line = '';
      for (let i = 0; i < 40; i++) {
        line += chars[Math.floor(Math.random() * chars.length)] + ' ';
      }
      setHistory(prev => [...prev, { type: 'matrix', text: line }]);
    }, 150);

    return () => clearInterval(interval);
  }, [matrixActive]);

  const quickLinks = [
    { label: 'neofetch', cmd: 'neofetch' },
    { label: 'whoami', cmd: 'whoami' },
    { label: 'nmap scan', cmd: 'nmap' },
    { label: 'matrix.sh', cmd: 'matrix' },
    { label: 'clear', cmd: 'clear' },
  ];

  return (
    <Box
      onClick={handleTerminalClick}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'Share Tech Mono', monospace",
        color: '#50fa7b',
        bgcolor: '#0a0d14',
        p: 1.5,
        borderRadius: '4px',
        overflow: 'hidden',
        boxSizing: 'border-box',
        cursor: 'text',
      }}
    >
      {/* Scrollable output area */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2, pr: 1 }}>
        {history.map((line, idx) => {
          if (line.type === 'prompt') {
            return (
              <Typography
                key={idx}
                sx={{
                  fontFamily: "'Share Tech Mono', monospace",
                  fontSize: '0.92rem',
                  color: '#00e5ff',
                  whiteSpace: 'pre-wrap',
                  mb: 0.5,
                }}
              >
                {line.text}
              </Typography>
            );
          } else if (line.type === 'matrix') {
            return (
              <Typography
                key={idx}
                sx={{
                  fontFamily: "'Share Tech Mono', monospace",
                  fontSize: '0.88rem',
                  color: '#22c55e',
                  whiteSpace: 'pre-wrap',
                  opacity: 0.8,
                  mb: 0.2,
                }}
              >
                {line.text}
              </Typography>
            );
          } else if (line.type === 'error') {
            return (
              <Typography
                key={idx}
                sx={{
                  fontFamily: "'Share Tech Mono', monospace",
                  fontSize: '0.9rem',
                  color: '#f87171',
                  whiteSpace: 'pre-wrap',
                  mb: 0.5,
                }}
              >
                {line.text}
              </Typography>
            );
          } else {
            return (
              <Typography
                key={idx}
                sx={{
                  fontFamily: "'Share Tech Mono', monospace",
                  fontSize: '0.9rem',
                  color: '#f3f4f6',
                  whiteSpace: 'pre-wrap',
                  mb: 0.5,
                }}
              >
                {line.text}
              </Typography>
            );
          }
        })}
        <div ref={bottomRef} />
      </Box>

      {/* Quick click command chips */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1.5,
          borderTop: '1px solid #1f2937',
          pt: 1.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: '#6b7280',
            alignSelf: 'center',
            mr: 1,
            fontSize: '0.75rem',
            userSelect: 'none',
          }}
        >
          QUICK PROTOCOLS:
        </Typography>
        {quickLinks.map((ql) => (
          <Box
            key={ql.label}
            onClick={(e) => {
              e.stopPropagation();
              executeCommand(ql.cmd);
            }}
            sx={{
              px: 1.2,
              py: 0.4,
              bgcolor: 'rgba(0, 229, 255, 0.08)',
              border: '1px solid rgba(0, 229, 255, 0.25)',
              borderRadius: '4px',
              color: '#00e5ff',
              fontSize: '0.75rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
              userSelect: 'none',
              '&:hover': {
                bgcolor: 'rgba(0, 229, 255, 0.2)',
                borderColor: '#00e5ff',
              },
            }}
          >
            {ql.label}
          </Box>
        ))}
      </Box>

      {/* Terminal Input Line */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography
          sx={{
            fontFamily: "'Share Tech Mono', monospace",
            fontSize: '0.92rem',
            color: '#00e5ff',
            mr: 1,
            userSelect: 'none',
          }}
        >
          root@kali:~#
        </Typography>
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
          disabled={matrixActive}
          style={{
            flexGrow: 1,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: '#f3f4f6',
            fontFamily: "'Share Tech Mono', monospace",
            fontSize: '0.92rem',
            caretColor: '#00e5ff',
          }}
        />
      </Box>
    </Box>
  );
}

export default Terminal;
